import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

/**
 * NPT-2243 / NPT-2277 — acknowledgements given in a confirm step (gate), kept across pages so one
 * given on the planner or hotel page isn't asked again at checkout for the same rate, and so the
 * checkout can send it with the booking as dispute evidence. Persisted with the rest of the store
 * (a reload between the hotel page and checkout keeps it). Keys are gate memo keys
 * (`refund_ack:<blockId>:<refund kind>:<changeability>`); entries older than a day are dropped.
 */
export type GateAckRecord = {
   gateId: string;
   copyVersion: string | null;
   refundabilityKind: 'FULL' | 'PARTIAL' | 'NONE' | null;
   amendable: boolean | null;
   blockId: string | null;
   surface: string | null;
   /** ISO time the traveller (or admin) accepted. */
   acceptedAt: string;
};

export const GATE_ACK_TTL_MS = 24 * 60 * 60 * 1000;

const initialState: { records: Record<string, GateAckRecord> } = { records: {} };

export const gateAckSlice = createSlice({
   name: 'gateAck',
   initialState,
   reducers: {
      recordGateAck(state, action: PayloadAction<{ key: string; record: GateAckRecord }>) {
         const cutoff = Date.now() - GATE_ACK_TTL_MS;
         for (const [key, record] of Object.entries(state.records)) {
            if (!(Date.parse(record.acceptedAt) > cutoff)) delete state.records[key];
         }
         state.records[action.payload.key] = action.payload.record;
      },
   },
});

export const { recordGateAck } = gateAckSlice.actions;

export default gateAckSlice;
